import React, { memo, useState, useEffect } from 'react';
import {
  Target,
  Shield,
  Users,
  Clock,
  Globe,
  Zap,
  LineChart,
  BrainCircuit,
  Code,
  Eye,
  ArrowRight,
  CheckCircle,
  Award,
  TrendingUp,
  Lightbulb,
  DollarSign,
  Mail,
  Building2
} from 'lucide-react';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { aboutUsPtBR } from '../../components/mappings/aboutUs.pt-br';
import SEOHead from '../../components/SEO/SEOHead';

// Keep these outside component for performance

// Utility function for color classes - Terminal Glassmorphism style
const getColorClasses = (color, variant = 'default') => {
  const colorMap = {
    emerald: {
      default: 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]',
      icon: 'text-emerald-400/80',
      bg: 'bg-white/[0.02]',
      border: 'border-emerald-500/20',
      hover: 'hover:bg-white/[0.03] hover:border-white/[0.08]'
    },
    violet: {
      default: 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]',
      icon: 'text-violet-400/70',
      bg: 'bg-white/[0.02]',
      border: 'border-violet-500/20',
      hover: 'hover:bg-white/[0.03] hover:border-white/[0.08]'
    },
    indigo: {
      default: 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]',
      icon: 'text-violet-400/70',
      bg: 'bg-white/[0.02]',
      border: 'border-violet-500/10',
      hover: 'hover:bg-white/[0.03] hover:border-white/[0.08]'
    },
    slate: {
      default: 'bg-white/[0.02] backdrop-blur-sm border border-white/[0.05]',
      icon: 'text-white/50',
      bg: 'bg-white/[0.02]',
      border: 'border-white/10',
      hover: 'hover:bg-white/[0.03] hover:border-white/[0.08]'
    }
  };
  return colorMap[color]?.[variant] || colorMap.emerald[variant];
};

// Memoized components
const ValueCard = memo(({ value, index }) => (
  <div className="group relative bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
    {/* Live indicator */}
    <div className="absolute top-6 right-6">
      <span className="w-1 h-1 bg-emerald-500/30 rounded-full" />
    </div>

    <div className="mb-4">
      <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">{value.color === 'emerald' ? 'CORE' : value.color === 'violet' ? 'PRINCIPLE' : value.color === 'indigo' ? 'ACCESS' : 'ETHICS'}</span>
    </div>

    <h3 className="text-sm text-white/70 font-medium mb-3">{value.title}</h3>
    <p className="text-xs text-white/50 leading-relaxed">{value.description}</p>
  </div>
));

const MetricCard = memo(({ metric }) => (
  <div className="text-center group">
    <div className="font-mono text-2xl text-emerald-400/80 group-hover:text-emerald-400/90 mb-2 transition-colors">
      {metric.number}
    </div>
    <div className="text-[10px] text-white/30 uppercase tracking-wider">{metric.label}</div>
  </div>
));

const TimelineItem = memo(({ item, index, isLast }) => (
  <div className="relative flex items-start">
    <div className="flex-shrink-0 mr-6">
      <div className={`w-2 h-2 rounded-full ${
        item.status === 'completed' ? 'bg-emerald-500/70' :
        item.status === 'current' ? 'bg-violet-500/70 animate-pulse' :
        'bg-white/20'
      }`}></div>
      {!isLast && <div className="w-[1px] h-16 bg-white/[0.05] ml-[3px] mt-2"></div>}
    </div>
    <div className="flex-grow pb-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono text-xs text-violet-400/70 hover:text-violet-400/90 transition-colors">
          {item.year}
        </span>
        {item.status === 'completed' && (
          <span className="text-[10px] text-emerald-400/60 uppercase tracking-wider">COMPLETED</span>
        )}
        {item.status === 'current' && (
          <span className="text-[10px] text-violet-400/70 uppercase tracking-wider">IN PROGRESS</span>
        )}
      </div>
      <h3 className="text-sm text-white/80 font-medium mb-2">{item.title}</h3>
      <p className="text-xs text-white/50 leading-relaxed">{item.description}</p>
    </div>
  </div>
));

const AboutUs = () => {
  const [visibleSections, setVisibleSections] = useState({});
  const isBrazilian = getBrazilianStatus();

  // Helper function to get translated text
  const t = (key) => {
    return isBrazilian ? (aboutUsPtBR[key] || key) : key;
  };

  // Core values with professional messaging
  const coreValues = [
    {
      icon: BrainCircuit,
      title: t("AI-Driven Excellence"),
      description: t("We leverage cutting-edge artificial intelligence and machine learning technologies to process vast datasets, delivering predictive insights that surpass traditional analytical methods."),
      color: "emerald"
    },
    {
      icon: Eye,
      title: t("Radical Transparency"),
      description: t("Our commitment to transparency means clear methodologies, honest probability assessments, and data-driven recommendations without misleading guarantees or false promises."),
      color: "violet"
    },
    {
      icon: Users,
      title: t("Democratized Access"),
      description: t("We make institutional-grade sports analytics accessible to individual users, breaking down barriers between professional and recreational sports analysis."),
      color: "indigo"
    },
    {
      icon: Shield,
      title: t("Ethical Innovation"),
      description: t("We prioritize responsible development and deployment of AI technologies, ensuring our platform promotes informed decision-making rather than impulsive behavior."),
      color: "slate"
    }
  ];

  const keyMetrics = [
    { number: "50M+", label: t("Data Points Processed Daily"), icon: LineChart },
    { number: "99.9", label: t("Platform Uptime"), icon: Target },
    { number: "25+", label: t("Sports Leagues Covered"), icon: Globe },
    { number: "24/7", label: t("Real-Time Analysis"), icon: Clock }
  ];

  const timeline = [
    {
      year: "2024 Q3",
      title: t("Company Foundation"),
      description: t("PickStar established by Carnegie Mellon and Vanderbilt alumni with expertise in AI and quantitative analysis."),
      status: "completed"
    },
    {
      year: "2024 Q4",
      title: t("Core Technology Development"),
      description: t("Proprietary machine learning algorithms developed and tested across historical sports data spanning multiple seasons."),
      status: "completed"
    },
    {
      year: "2025 Q1",
      title: t("Alpha Testing Phase"),
      description: t("Internal validation of predictive models with promising results across major professional sports leagues."),
      status: "completed"
    },
    {
      year: "2025 Q2",
      title: t("Beta Platform Launch"),
      description: t("Limited release to select users, coinciding with March Madness tournament season for comprehensive testing."),
      status: "completed"
    },
    {
      year: "2025 Q2",
      title: t("V2 Launch - Agentic Backend"),
      description: t("Enhanced agentic backend deployment with significantly expanded processing power and coverage across additional sports leagues."),
      status: "completed"
    },
    {
      year: "2025 Q4",
      title: t("Full Commercial Release"),
      description: t("Complete platform deployment with expanded sports coverage, personalized insights, and scalable infrastructure."),
      status: "current"
    }
  ];

  const teamHighlights = [
    t("Carnegie Mellon University & Vanderbilt University alumni"),
    t("Combined 15+ years in machine learning and data science"),
    t("Former experience at leading technology companies"),
    t("Published research in artificial intelligence applications"),
    t("Passionate sports enthusiasts and analytical thinkers")
  ];

  const investmentHighlights = [
    t("Massive addressable market in sports analytics and entertainment"),
    t("Proprietary AI technology with proven accuracy metrics"),
    t("Strong founding team with deep technical expertise"),
    t("Clear monetization strategy and scalable business model"),
    t("Early traction with promising user engagement metrics")
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const sections = document.querySelectorAll('[id^="section-"]');
    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  const isVisible = (sectionId) => visibleSections[sectionId];

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section id="section-hero" className={`relative py-8 md:py-12 bg-gradient-to-b from-white/[0.02] to-transparent transition-all duration-1000 ${isVisible('section-hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div className="relative max-w-screen-xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            {/* Live Status */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 text-[10px] text-emerald-400/80 uppercase tracking-[0.2em]">
                <span className="w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
                AI-POWERED ANALYTICS PLATFORM
              </div>
            </div>

            <h1 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">
              TRANSFORMING SPORTS ANALYSIS
            </h1>
            <h2 className="text-4xl md:text-5xl font-bold text-white/95 mb-4">
              {t("Transforming Sports Analysis Through")}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400">{t(" Artificial Intelligence")}</span>
            </h2>

            <p className="text-sm text-white/50 mb-6 leading-relaxed max-w-3xl mx-auto">
              {t("PickStar leverages advanced machine learning to deliver institutional-grade sports analytics, making sophisticated predictive insights accessible to individual users through transparent, data-driven methodologies.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/login" className="px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200 shadow-lg shadow-emerald-500/10 flex items-center justify-center">
                {t("Sign Up Now for Free")}
                <ArrowRight className="w-3 h-3 ml-2" />
              </a>
              <a href="/pricing" className="px-6 py-3 bg-white/[0.02] backdrop-blur-sm border border-violet-500/20 rounded-md text-[11px] text-white/70 uppercase tracking-wider hover:bg-white/[0.05] hover:border-violet-500/30 hover:text-violet-400/80 transition-all duration-200 flex items-center justify-center">
                {t("Upgrade to Pro")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section id="section-mission" className={`py-8 md:py-10 transition-all duration-1000 delay-200 ${isVisible('section-mission') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 md:p-8 relative overflow-hidden">
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">OUR MISSION</h2>
              <h3 className="text-2xl font-bold text-white/95 mb-4">{t("Our Mission")}</h3>
              <p className="text-sm text-white/70 leading-relaxed mb-6 max-w-3xl mx-auto">
                {t('"To democratize access to sophisticated sports analytics through transparent AI technology, empowering users with institutional-grade insights while maintaining the highest standards of ethical data science and predictive modeling."')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {keyMetrics.map((metric, index) => (
                  <MetricCard key={index} metric={metric} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section id="section-values" className={`py-8 md:py-10 transition-all duration-1000 delay-300 ${isVisible('section-values') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="mb-6 pb-4 border-b border-white/[0.05]">
            <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">CORE VALUES</h2>
            <h3 className="text-2xl font-bold text-white/95 mb-4">{t("Core Values")}</h3>
            <p className="text-sm text-white/50 max-w-3xl">
              {t("Our foundational principles guide every aspect of our platform development and user experience design.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {coreValues.map((value, index) => (
              <ValueCard key={value.title} value={value} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="section-team" className={`py-8 md:py-10 transition-all duration-1000 delay-400 ${isVisible('section-team') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">OUR TEAM</h2>
              <h3 className="text-xl font-bold text-white/95 mb-4">{t("Our Team")}</h3>
              <p className="text-sm text-white/50 mb-4 leading-relaxed">
                {t("PickStar was founded by a team of data scientists and engineers from leading academic institutions, combining deep expertise in artificial intelligence with a passion for sports analytics.")}
              </p>
              <div className="space-y-3">
                {teamHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2 text-xs text-white/40 hover:text-white/60 transition-colors">
                    <span className="text-emerald-400/40 mt-0.5">→</span>
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
              <div className="text-center">
                <div className="mb-4">
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">LEADERSHIP</span>
                </div>
                <h3 className="text-sm text-white/70 font-medium mb-4">{t("Leadership Excellence")}</h3>
                <p className="text-xs text-white/50 leading-relaxed">
                  {t("Our leadership team brings together decades of experience in machine learning, quantitative analysis, and sports data science from top-tier institutions and technology companies.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section id="section-timeline" className={`py-8 md:py-10 bg-gradient-to-b from-white/[0.01] to-transparent transition-all duration-1000 delay-500 ${isVisible('section-timeline') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-6 pb-4 border-b border-white/[0.05]">
            <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">COMPANY TIMELINE</h2>
            <h3 className="text-2xl font-bold text-white/95 mb-4">{t("Company Timeline")}</h3>
            <p className="text-sm text-white/50">
              {t("Our journey from conception to market-leading AI sports analytics platform.")}
            </p>
          </div>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <TimelineItem
                key={index}
                item={item}
                index={index}
                isLast={index === timeline.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Investor Section */}
      <section id="section-investors" className={`py-8 md:py-10 transition-all duration-1000 delay-600 ${isVisible('section-investors') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-8 hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200">
              <div className="text-center">
                <div className="mb-4">
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">INVESTMENT</span>
                </div>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/20 rounded-lg mb-4">
                  <Building2 className="w-8 h-8 text-emerald-400/70" />
                </div>
                <h3 className="text-lg text-white/80 font-medium mb-3">{t("Investment Opportunity")}</h3>
                <p className="text-xs text-white/50 mb-6 leading-relaxed">
                  {t("We're building the future of sports analytics with AI. Our platform represents a significant opportunity in the rapidly growing sports technology market.")}
                </p>
                <div className="inline-flex items-center gap-2 bg-white/[0.02] border border-emerald-500/20 text-emerald-400/70 px-4 py-2 rounded-md text-[11px] font-medium uppercase tracking-wider">
                  <DollarSign className="w-3.5 h-3.5" />
                  {t("Seeking Strategic Partners")}
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">INVESTORS</h2>
              <h3 className="text-2xl font-bold text-white/95 mb-4">{t("Interested in Investing?")}</h3>
              <p className="text-sm text-white/70 mb-6 leading-relaxed">
                {t("PickStar represents a unique opportunity to invest in cutting-edge AI technology applied to the massive sports analytics market. We're looking for strategic partners who share our vision for democratizing sports intelligence.")}
              </p>
              <div className="space-y-3 mb-6">
                {investmentHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2 text-xs text-white/40 hover:text-white/60 transition-colors">
                    <TrendingUp className="w-4 h-4 text-emerald-400/40 mt-0.5 flex-shrink-0" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
              <a
                href="mailto:engineering@pickstarai.com?subject=Investment Inquiry - PickStar AI"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200 shadow-lg shadow-emerald-500/10"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t("Contact Our Team")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="section-cta" className={`py-8 md:py-10 transition-all duration-1000 delay-700 ${isVisible('section-cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-6 md:p-8 relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-2">GET STARTED</h2>
              <h3 className="text-2xl font-bold text-white/95 mb-4">{t("Ready to Experience the Future?")}</h3>
              <p className="text-sm text-white/50 mb-6 max-w-2xl mx-auto leading-relaxed">
                {t("Join our beta program and gain early access to institutional-grade sports analytics powered by cutting-edge artificial intelligence.")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/login" className="px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200 shadow-lg shadow-emerald-500/10 flex items-center justify-center">
                  {t("Sign Up Now for Free")}
                  <ArrowRight className="w-3 h-3 ml-2" />
                </a>
                <a href="/pricing" className="px-6 py-3 bg-white/[0.02] backdrop-blur-sm border border-violet-500/20 rounded-md text-[11px] text-white/70 uppercase tracking-wider hover:bg-white/[0.05] hover:border-violet-500/30 hover:text-violet-400/80 transition-all duration-200 flex items-center justify-center">
                  {t("Upgrade to Pro")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="py-6 border-t border-white/[0.05]">
        <div className="max-w-screen-xl mx-auto px-6 text-center">
          <p className="text-[10px] text-white/20 leading-relaxed uppercase tracking-wider">
            {t('*AI-powered sports insights is intended for entertainment purposes only. Generated insights or "prediction" content by AI varies by sport, model complexity, and generative AI techniques employed. Certain scenarios may produce different results. Nothing in sports is guaranteed, and past performance does not indicate future results. Please use AI generated content responsibly.')}
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default memo(AboutUs);
